import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWordSetById } from '../../data';
import VirtualKeyboard from './VirtualKeyboard';
import WordDisplay from './WordDisplay';
import ProgressBar from '../common/ProgressBar';
import ResultModal from '../common/ResultModal';
import Toast from '../common/Toast';
import { useTyping } from '../../hooks/useTyping';
import { useToast } from '../../hooks/useToast';
import './TypingScreen.css';

function TypingScreen() {
  const { setId } = useParams();
  const navigate = useNavigate();
  const [wordSet, setWordSet] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [mistakes, setMistakes] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [activeKey, setActiveKey] = useState(null); // eslint-disable-line no-unused-vars

  const { isKorean } = useTyping();
  const { toasts, showToast, hideToast } = useToast();

  // 음성 재생 함수
  const playSound = useCallback((soundPath) => {
    // 설정에서 음성 효과가 활성화되어 있는지 확인
    const soundEnabled = localStorage.getItem('soundEffects') !== 'false';
    if (!soundEnabled || !soundPath) return;

    try {
      const audio = new Audio(soundPath);
      audio.volume = 0.5; // 볼륨 50%
      audio.play().catch(error => {
        console.warn('Sound playback failed:', error);
      });
    } catch (error) {
      console.warn('Sound creation failed:', error);
    }
  }, []);

  useEffect(() => {
    const loadedSet = getWordSetById(setId);
    if (!loadedSet) {
      navigate('/');
      return;
    }
    setWordSet(loadedSet);
    setStartTime(Date.now());
  }, [setId, navigate]);

  const currentWord = wordSet?.words[currentWordIndex];
  const currentStep = currentWord?.steps[currentStepIndex];
  const targetWord = currentWord?.word;
  
  // 현재 타이핑 중인 글자의 인덱스 계산
  const getCurrentCharIndex = () => {
    if (!targetWord || !currentStep) return 0;
    
    // 현재 단계의 문자열에서 어느 글자를 타이핑 중인지 계산
    const currentChar = currentStep.char;
    if (!currentChar) return 0;
    
    // 완성된 글자의 개수를 세어서 현재 인덱스 계산
    const completeChars = currentChar.replace(/[ㄱ-ㅎㅏ-ㅣ]/g, '').length;
    
    // 현재 조합 중인 글자가 있으면 그 인덱스, 없으면 마지막 완성된 글자의 다음 인덱스
    if (currentChar.match(/[ㄱ-ㅎㅏ-ㅣ]$/)) {
      return completeChars;
    }
    
    return Math.min(completeChars - 1, targetWord.length - 1);
  };
  
  // 다음 입력해야 할 키 계산
  const getNextKey = () => {
    if (!currentStep) return null;
    
    if (isKorean(wordSet?.lang)) {
      // 한글: JSON에서 key 필드 사용
      return currentStep.key || null;
    } else {
      // 영어: JSON에서 key와 shift 필드 사용
      return {
        key: currentStep.key || null,
        shift: currentStep.shift || false
      };
    }
  };

  const nextChar = getNextKey();

  const handleWordComplete = useCallback(() => {
    if (currentWordIndex < wordSet.words.length - 1) {
      setTimeout(() => {
        setCurrentWordIndex(prev => prev + 1);
        setCurrentStepIndex(0);
        setUserInput('');
      }, 500);
    } else {
      // 모든 단어 완료
      setShowResult(true);
    }
  }, [currentWordIndex, wordSet]);

  const handleKeyPress = useCallback((e) => {
    if (!currentWord || showResult) return;

    const key = e.key;
    console.log('Key pressed:', key, 'Expected:', nextChar, 'Type:', typeof nextChar);
    setActiveKey(e.code);
    setTimeout(() => setActiveKey(null), 100);

    // 언어 체크 로직
    const checkLanguageMismatch = () => {
      if (key.length !== 1) return false;
      
      if (isKorean(wordSet.lang)) {
        // 한글을 입력해야 하는데 영어를 입력한 경우
        if (/[a-zA-Z]/.test(key)) {
          showToast('한글로 전환해주세요! (한/영 키를 눌러주세요)', 'warning', 5000);
          return true;
        }
      } else {
        // 영어를 입력해야 하는데 한글을 입력한 경우
        if (/[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(key)) {
          showToast('영어로 전환해주세요! (한/영 키를 눌러주세요)', 'warning', 5000);
          return true;
        }
      }
      return false;
    };

    // 언어가 맞지 않으면 입력 무시
    if (checkLanguageMismatch()) {
      return;
    }

    if (key === 'Backspace') {
      if (userInput.length > 0) {
        const newInput = userInput.slice(0, -1);
        setUserInput(newInput);
        
        // 백스페이스 후 적절한 단계로 이동
        let newStepIndex = 0;
        for (let i = currentWord.steps.length - 1; i >= 0; i--) {
          if (currentWord.steps[i].char === newInput) {
            newStepIndex = i;
            break;
          }
        }
        setCurrentStepIndex(newStepIndex);
      }
      return;
    }

    if (key.length !== 1) return;

    // 예상되는 다음 키와 일치하는지 확인
    let expectedKey = nextChar;
    if (isKorean(wordSet.lang)) {
      // 한글: 직접 비교
      if (key !== expectedKey) {
        setMistakes(prev => prev + 1);
        return;
      }
    } else {
      // 영어: 객체인 경우 key 필드와 비교
      if (typeof expectedKey === 'object') {
        // shift가 필요한 경우 대문자로 비교, 아니면 소문자로 비교
        const expectedChar = expectedKey.shift ? expectedKey.key.toUpperCase() : expectedKey.key.toLowerCase();
        if (key !== expectedChar) {
          setMistakes(prev => prev + 1);
          return;
        }
      } else {
        if (key !== expectedKey) {
          setMistakes(prev => prev + 1);
          return;
        }
      }
    }

    const newInput = userInput + key;

    if (isKorean(wordSet.lang)) {
      // 한글: 단계별 이동
      setUserInput(newInput);
      
      // 현재 단계 완료 시 음성 재생
      if (currentStep.sound) {
        playSound(currentStep.sound);
      }

      // 다음 단계로 이동
      if (currentStepIndex < currentWord.steps.length - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else {
        // 마지막 단계면 완료
        handleWordComplete();
      }
    } else {
      // 영어: 문자 누적
      setUserInput(newInput);
      
      // 현재 단계 완성 확인
      if (newInput === currentStep.char) {
        // 단계 완료 시 음성 재생
        if (currentStep.sound) {
          playSound(currentStep.sound);
        }

        if (currentStepIndex < currentWord.steps.length - 1) {
          setCurrentStepIndex(prev => prev + 1);
        } else {
          handleWordComplete();
        }
      }
    }
  }, [currentWord, currentStep, currentStepIndex, userInput, wordSet, showResult, isKorean, nextChar, handleWordComplete, showToast, playSound]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const handleRestart = () => {
    setCurrentWordIndex(0);
    setCurrentStepIndex(0);
    setUserInput('');
    setMistakes(0);
    setStartTime(Date.now());
    setShowResult(false);
  };

  const handleNext = () => {
    navigate('/');
  };

  if (!wordSet) {
    return <div className="loading">로딩 중...</div>;
  }

  const progress = wordSet && currentWord ? 
    ((currentWordIndex + (currentStepIndex / currentWord.steps.length)) / wordSet.words.length) * 100 : 0;
  const elapsedTime = showResult ? Math.floor((Date.now() - startTime) / 1000) : 0;

  return (
    <div className="typing-screen">
      <header className="typing-header">
        <Link to="/" className="back-button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5m0 0l7 7m-7-7l7-7"></path>
          </svg>
        </Link>
        <div className="set-info">
          <h1>{wordSet.name}</h1>
          <p>{wordSet.description}</p>
        </div>
      </header>

      <main className="typing-main">
        <ProgressBar 
          progress={progress} 
          current={currentWordIndex + 1} 
          total={wordSet.words.length} 
        />

        <WordDisplay
          targetWord={targetWord}
          currentStep={currentStep?.char}
          userInput={currentStep?.char || ''}
          description={currentWord?.description}
          image={currentWord?.image}
          currentCharIndex={getCurrentCharIndex()}
        />

        <VirtualKeyboard
          language={wordSet.lang}
          nextKey={nextChar}
          currentInput={userInput}
          targetChar={currentStep?.char}
        />
      </main>

      {showResult && (
        <ResultModal
          wordCount={wordSet.words.length}
          mistakes={mistakes}
          time={elapsedTime}
          onRestart={handleRestart}
          onNext={handleNext}
        />
      )}

      {/* 토스트 알림 */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default TypingScreen;